import queryClient from "single-spa/react-app/query-client";

import type {
    ProKeywordGroup,
    CreateKeywordGroupPayload,
    IUpdateKeywordGroupPayload,
    ShareKeywordGroupPayload,
    DuplicateKeywordGroupPayload,
} from "../keywordGroups.apiClient.types";
import {
    keywordGroupsApiClientOptions,
    keywordGroupsApiClientKeys,
    createKeywordGroup,
    updateKeywordGroup,
    deleteKeywordGroup,
    shareKeywordGroup,
    removeKeywordGroupSharing,
    duplicateKeywordGroup,
} from "../queries/keywordGroupsApiClientQueries";

export const keywordGroupsApiClientQueryClient = {
    getUserGroups: async (): Promise<ProKeywordGroup[]> => {
        return await queryClient.fetchQuery<ProKeywordGroup[]>(keywordGroupsApiClientOptions.user());
    },

    getSharedGroups: async (): Promise<ProKeywordGroup[]> => {
        return await queryClient.fetchQuery<ProKeywordGroup[]>(keywordGroupsApiClientOptions.shared());
    },

    getGroupById: async (id: string): Promise<ProKeywordGroup | undefined> => {
        return await queryClient.fetchQuery<ProKeywordGroup | undefined>(keywordGroupsApiClientOptions.byId(id));
    },

    getAllGroups: async (): Promise<{
        userGroups: ProKeywordGroup[];
        sharedGroups: ProKeywordGroup[];
    }> => {
        const [userGroups, sharedGroups] = await Promise.all([
            queryClient.fetchQuery<ProKeywordGroup[]>(keywordGroupsApiClientOptions.user()),
            queryClient.fetchQuery<ProKeywordGroup[]>(keywordGroupsApiClientOptions.shared()),
        ]);

        return { userGroups, sharedGroups };
    },

    createGroup: async (group: CreateKeywordGroupPayload): Promise<ProKeywordGroup | undefined> => {
        await createKeywordGroup(group);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
        const newGroupList = await keywordGroupsApiClientQueryClient.getUserGroups();
        const newGroup = newGroupList.find((g) => g.Name === group.Name);
        return newGroup;
    },

    updateGroup: async (group: IUpdateKeywordGroupPayload): Promise<ProKeywordGroup | undefined> => {
        await updateKeywordGroup(group);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.byId(group.Id) });
        const updatedGroup = await keywordGroupsApiClientQueryClient.getGroupById(group.Id);
        return updatedGroup;
    },

    deleteGroup: async (group: ProKeywordGroup): Promise<void> => {
        await deleteKeywordGroup(group.Id, group.Name, group.Keywords);

        queryClient.setQueryData(keywordGroupsApiClientKeys.user(), (old: ProKeywordGroup[] = []) =>
            old.filter((g) => g.Id !== group.Id),
        );

        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
    },

    shareGroup: async (groupId: string, payload: ShareKeywordGroupPayload): Promise<void> => {
        await shareKeywordGroup(groupId, payload);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.shared() });
    },

    removeGroupSharing: async (groupId: string): Promise<void> => {
        await removeKeywordGroupSharing(groupId);
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.shared() });
    },

    duplicateGroup: async (payload: DuplicateKeywordGroupPayload): Promise<ProKeywordGroup> => {
        const duplicatedGroup = await duplicateKeywordGroup(payload);

        queryClient.setQueryData(keywordGroupsApiClientKeys.user(), (old: ProKeywordGroup[] = []) => [
            ...old,
            duplicatedGroup,
        ]);

        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });

        return duplicatedGroup;
    },

    prefetchUserGroups: async (): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsApiClientOptions.user());
    },

    prefetchSharedGroups: async (): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsApiClientOptions.shared());
    },

    prefetchGroupById: async (id: string): Promise<void> => {
        await queryClient.prefetchQuery(keywordGroupsApiClientOptions.byId(id));
    },

    invalidateAll: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.all });
    },

    invalidateUserGroups: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.user() });
    },

    invalidateSharedGroups: async (): Promise<void> => {
        await queryClient.invalidateQueries({ queryKey: keywordGroupsApiClientKeys.shared() });
    },
};
